import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const FavoritesContext = createContext(null);

export function FavoritesProvider({ children }) {
  const { user } = useAuth();
  const [ids, setIds] = useState(new Set());

  const refresh = useCallback(async () => {
    if (!user) { setIds(new Set()); return; }
    try {
      const { data } = await api.get("/favorites/ids");
      setIds(new Set(data.ids));
    } catch { setIds(new Set()); }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const isFav = useCallback((id) => ids.has(id), [ids]);

  const toggle = useCallback(async (id) => {
    if (!user) return { needAuth: true };
    // optimistic
    setIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    try {
      const { data } = await api.post(`/favorites/${id}`);
      setIds((prev) => {
        const next = new Set(prev);
        data.favorited ? next.add(id) : next.delete(id);
        return next;
      });
      return { favorited: data.favorited };
    } catch {
      refresh();
      return { error: true };
    }
  }, [user, refresh]);

  return (
    <FavoritesContext.Provider value={{ ids, isFav, toggle, count: ids.size }}>
      {children}
    </FavoritesContext.Provider>
  );
}

export const useFavorites = () => useContext(FavoritesContext);
